import { FC } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ChannelSlice } from '../forecasting.types';
import { formatCurrency, formatPercent } from '../utils/forecastingAggregations';
import { ChartTitle, Panel } from '../Forecasting.styles';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

const CHANNEL_COLORS = ['#f3b547', '#f66d6b', '#776194', '#6161d7', '#0b1775', '#0a0a36', '#8c9bb5'];

interface ChannelDonutChartProps {
  data: ChannelSlice[];
  metricLabel: string;
  title?: string;
}

export const ChannelDonutChart: FC<ChannelDonutChartProps> = ({ data, metricLabel, title }) => (
  <Panel>
    <ChartTitle>{title ?? `Channel mix — ${metricLabel}`}</ChartTitle>
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={95}
          paddingAngle={1}
        >
          {data.map((entry, i) => (
            <Cell key={entry.name} fill={CHANNEL_COLORS[i % CHANNEL_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            background: '#1a2550',
            border: `1px solid rgba(${APP_SHELL_THEME.overlayRgb}, 0.15)`,
            borderRadius: 6,
            fontSize: 12
          }}
          formatter={(value: number, _name: string, item: { payload?: ChannelSlice }) => {
            const pct = item.payload?.percent ?? 0;
            return [`${formatCurrency(value)} (${formatPercent(pct, 1)})`, item.payload?.name];
          }}
        />
      </PieChart>
    </ResponsiveContainer>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', marginTop: 8, fontSize: 11, fontWeight: 400, color: APP_SHELL_THEME.dataText }}>
      {data.slice(0, 8).map((d, i) => (
        <span key={d.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <i
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: CHANNEL_COLORS[i % CHANNEL_COLORS.length],
              display: 'inline-block'
            }}
          />
          {d.name} {formatPercent(d.percent, 0)}
        </span>
      ))}
    </div>
  </Panel>
);
